/*
  APD 2026-09-25 — our own wishlist, replacing the Swish / Wishlist King app (owner: no paid apps).

  Saved items live in the shopper's own browser (localStorage, key `apd:wishlist`) as a list of
  product handles, newest first. No app, no account needed, nothing sent anywhere. The trade-off,
  accepted by the owner: the list does not follow a shopper from one device to another.

  Wiring, all by data attributes so any template can use it:
    [data-apd-wishlist="<handle>"]  a heart button; toggles that product (snippets/product-card.liquid)
    [data-apd-wishlist-count]       gets the number of saved items, plus `is-visible` when > 0
    [data-apd-wishlist-grid]        the saved-items page (sections/apd-wishlist.liquid): filled with
                                    the theme's own product cards, fetched one per product through
                                    templates/product.apd-wishlist-card.liquid
*/
(() => {
  const KEY = 'apd:wishlist';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && h) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (error) {
      // Private mode or blocked storage: the heart still flips for this page view.
    }
  };

  let list = read();
  const has = (handle) => list.includes(handle);

  // Only touch the DOM when something actually differs: the observer below reacts to DOM changes,
  // so an unconditional write would feed itself for ever.
  const setAttr = (el, name, value) => {
    if (value == null) return;
    if (el.getAttribute(name) !== String(value)) el.setAttribute(name, value);
  };

  const paint = () => {
    document.querySelectorAll('[data-apd-wishlist]').forEach((button) => {
      const saved = has(button.dataset.apdWishlist);
      setAttr(button, 'aria-pressed', saved);
      setAttr(button, 'aria-label', saved ? button.dataset.labelRemove : button.dataset.labelAdd);
    });

    document.querySelectorAll('[data-apd-wishlist-count]').forEach((el) => {
      const text = String(list.length);
      if (el.textContent !== text) el.textContent = text;
      if (el.classList.contains('is-visible') !== list.length > 0) el.classList.toggle('is-visible', list.length > 0);
    });
  };

  const announce = (handle) => {
    document.dispatchEvent(new CustomEvent('apd:wishlist:change', {
      detail: { handle, saved: has(handle), list: [...list] }
    }));
  };

  const toggle = (handle) => {
    list = has(handle) ? list.filter((h) => h !== handle) : [handle, ...list];
    write(list);
    paint();
    announce(handle);
  };

  const remove = (handle) => {
    if (has(handle)) toggle(handle);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-apd-wishlist]');
    if (!button) return;
    event.preventDefault();
    toggle(button.dataset.apdWishlist);
  });

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    list = read();
    paint();
    document.dispatchEvent(new CustomEvent('apd:wishlist:change', { detail: { handle: null, list: [...list] } }));
  });

  // Cards arrive after load (filters, carousels, quick add, the saved-items page itself).
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      paint();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.APDWishlist = { get list() { return [...list]; }, has, toggle, remove };
  paint();

  /* ---------------------------------------------------------------------------------------------
     The saved-items page
     --------------------------------------------------------------------------------------------- */
  const grid = document.querySelector('[data-apd-wishlist-grid]');
  if (!grid) return;

  const section = grid.closest('[data-apd-wishlist-section]');
  const empty = section && section.querySelector('[data-apd-wishlist-empty]');
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const showEmpty = () => {
    const isEmpty = !grid.querySelector('product-card');
    grid.hidden = isEmpty;
    if (empty) empty.hidden = !isEmpty;
  };

  const cardFor = async (handle) => {
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=apd-wishlist-card`);
      // Only a product that is truly gone is dropped from the list; a network blip keeps it.
      if (response.status === 404) return { handle, gone: true };
      if (!response.ok) return { handle };
      return { handle, html: await response.text() };
    } catch (error) {
      return { handle };
    }
  };

  let renderId = 0;
  const render = async () => {
    const id = ++renderId;
    const results = await Promise.all(list.map(cardFor));
    if (id !== renderId) return; // a newer render started while this one was fetching

    grid.innerHTML = '';
    results.forEach(({ handle, html, gone }) => {
      if (gone) {
        list = list.filter((h) => h !== handle);
        write(list);
        return;
      }
      if (!html) return;
      const holder = document.createElement('div');
      holder.innerHTML = html.trim();
      const card = holder.querySelector('product-card');
      if (card) {
        card.dataset.apdWishlistItem = handle;
        grid.appendChild(card);
      }
    });
    paint();
    showEmpty();
    grid.removeAttribute('aria-busy');
  };

  document.addEventListener('apd:wishlist:change', (event) => {
    const { handle, saved } = event.detail;
    if (handle && saved === false) {
      const card = grid.querySelector(`[data-apd-wishlist-item="${CSS.escape(handle)}"]`);
      if (card) card.remove();
      showEmpty();
    } else {
      render();
    }
  });

  render();
})();
